import { DateLike, normalizeDate } from '@apparel/core/date-time';
import { CategoryType } from '@model/Category';
import { OrderItem } from '@model/OrderItem';
import { ShippingLocationType } from '@model/ShippingLocation';
import { ActiveProps } from '@redux-traits/ActiveItem';
import { RouterComponentProps } from '../Types/Router';
import { Customer, CustomerType } from './Customer';
import { Location, LocationType } from './Location';
import { Model, ModelClass } from './Model';
import { Salesman, SalesmanType } from './Salesman';
import { ShippingMethodType } from './ShippingMethod';
import { TestPrintsStatus } from './TestPrints';
import { UserType } from './User';

export const ORDER_TYPES = ['Completed', 'Pending Payment'];

export enum ORDER_STATUS {
    ACTIVE = 'Active',
    CANCELLED = 'Cancelled',
    COMPLETED = 'Complete',
    PLACED = 'Placed',
    INCOMPLETE = 'Incomplete',
    UNSHIPPED = 'Unshipped',
    SHIPPED = 'Shipped',
    PARTIALLY_SHIPPED = 'Partially Shipped',
    READY = 'Ready',
    AWAITING_PAYMENT = 'Awaiting Payment',
    ARCHIVED = 'Archive',
    UNDER_REVIEW = 'Under Review',
    PO_REJECTED = 'PO Rejected',
}

export const REDO_REASONS = [
    { id: 1, name: 'Color Error' },
    { id: 2, name: 'Sizing Error' },
    { id: 3, name: 'Defect' },
    { id: 4, name: 'Quality Issue' },
    { id: 5, name: 'Missing Item' },
];

export enum ORDER_TYPE {
    STANDARD_ORDER = 1,
    ADD_ON_ORDER = 2,
    REDO_ORDER = 3,
    RE_ORDER = 4,
    CUSTOM_SAMPLE = 5,
    REPEAT_ORDER = 7,
}

/** Selectable tags shown in the tag picker. */
export const TAGS = [
    { id: 1, name: 'VIP', bgColor: '#DAB5F8' },
    { id: 2, name: 'MEGA-VIP', bgColor: '#FFE6A6' },
    { id: 3, name: 'One-time Event', bgColor: '#B4F4A9' },
    { id: 5, name: 'Deadline', bgColor: '#FFB6A6' },
    { id: 6, name: 'Add Rush', bgColor: '#F8B5B5' },
    { id: 8, name: 'Digital Proof Required', bgColor: '#A6D4F8' },
    { id: 9, name: 'Physical Proof Required', bgColor: '#F8D4A6' },
    { id: 10, name: 'Homefield Advantage', bgColor: '#C5F8B5' },
    { id: 11, name: 'Templated', bgColor: '#F8B5E0' },
    { id: 12, name: 'Urgent', bgColor: '#FFB3A6' },
];

/** Legacy tags: shown on existing orders and removable, but cannot be added. */
export const LEGACY_TAGS = [
    { id: 4, name: 'Custom Sample', bgColor: '#B5F8F4' },
    { id: 7, name: 'PO', bgColor: '#B5F8F4' },
];

/** All tags used for display / lookup (includes legacy). */
export const ALL_TAGS = [...TAGS, ...LEGACY_TAGS];

export const ORDER_TYPE_OPTIONS = [
    { id: 1, name: 'Standard Order', reason: null },
    { id: 2, name: 'Add-on Order', reason: 'Customer wants extra items' },
    { id: 3, name: 'Redo Order', reason: 'Factory Error' },
    { id: 4, name: 'Re-order', reason: 'GameBreaker Error' },
    { id: 5, name: 'Custom Sample', reason: null },
    { id: 7, name: 'Repeat Order', reason: null },
];

export const orderTypeNeedsDesignValidation = (orderTypeId?: number) =>
    orderTypeId === ORDER_TYPE.ADD_ON_ORDER || orderTypeId === ORDER_TYPE.REPEAT_ORDER;

export const DESIGN_INELIGIBLE_ORDER_TYPE_NOTICE =
    'Order type was reset to Standard because a design does not qualify.';

export const notifyIfDesignResetOrderType = (
    previousType: number | undefined,
    nextType: number | undefined,
    notify: { info: (message: string) => void },
) => {
    const wasAddonOrRepeat = previousType === ORDER_TYPE.ADD_ON_ORDER || previousType === ORDER_TYPE.REPEAT_ORDER;

    if (wasAddonOrRepeat && nextType === ORDER_TYPE.STANDARD_ORDER) {
        notify.info(DESIGN_INELIGIBLE_ORDER_TYPE_NOTICE);
    }
};

export const ADDON_ORDER_MAX_SOURCE_AGE_MONTHS = 2;

export const isEligibleForAddonOrder = (order?: { ready_at?: DateLike }): boolean => {
    const anchorDate = order?.ready_at;
    if (!anchorDate) {
        return true;
    }

    return normalizeDate(anchorDate).isSameOrAfter(
        normalizeDate().subtract(ADDON_ORDER_MAX_SOURCE_AGE_MONTHS, 'months'),
    );
};

export enum REDO_REASON_TYPE {
    COLOR_ERROR = 1,
    SIZING_ERROR = 2,
    DEFECT = 3,
    QUALITY_ISSUE = 4,
    MISSING_ITEM = 5,
}

export const RE_ORDER_REASONS = [
    { id: 6, name: 'Operational Error' },
    { id: 7, name: 'Sales Rep Error' },
    { id: 8, name: 'Other' },
];

export enum PAYMENT_METHODS_TYPES {
    CREDIT_CARD = 1,
    PURCHASE_ORDER = 2,
    ACH = 3,
    PAY_LATER = 4,
    PAID_IN_FULL_WITH_CREDIT = 5,
    PAID_OUTSIDE_PLATFORM = 6,
    PAYPAL = 7,
    MAIL_A_CHECK = 8,
    GOOGLE_PAY = 9,
    APPLE_PAY = 10,
    AFTER_PAY = 11,
    AFFIRM = 12,
    ECHECK = 13,
    PAY_DIRECTLY_TO_WHOLESALER = 14,
}

export enum MAIL_A_CHECK_STATUS {
    REJECTED = 0,
    APPROVED = 1,
}

export interface OrderType extends ModelClass {
    promo_code: string;
    coupon: any;
    coupon_id: number;
    sales_tax_formula: any;
    production_file_count: number;
    can_create_shipment: boolean;
    paid_at: any;
    ready_at: any;
    placed_by: any;
    placed_at: any;
    due_date: any;
    total_items: any;
    tags: any;
    production_files: any[];
    previous_orders: number[];
    order_type_id: number;
    shipments: any;
    order_status: string;
    payment_status: string;
    stripe_charge: any;
    currentStage: any;
    id: number;
    shipping_method_id: number;
    salesman_id: number;
    salesman: SalesmanType;
    customer_id: number;
    customer: CustomerType;
    promo_code_id: number;
    location_id: number;
    isInternationalOrder: boolean;
    has_tracking_number: boolean;
    location: LocationType;
    discount: string;
    discount_name: string;
    placement_date: string;
    factories_ship_by_date: string;
    status: string;
    covcare_order_status: string;
    order_factories: any;
    completion_time_estimate: string;
    completion_time: string;
    type: string;
    invoice_number: string;
    items: OrderItem[];
    current_stage: any;
    stage_statuses: any;
    stage_status: string;
    important: boolean;
    placed: boolean;
    overdue: boolean;
    category_id: number;
    category_name: string;
    category: CategoryType;
    name: string;
    created_at: Date;
    notes: string;
    stripe_charge_id: string;
    designs_submitted: boolean;
    roster_submitted: boolean;
    payment_method: number;
    quantity: number;
    purchased: boolean;
    payment_data: any;
    shipping_method: ShippingMethodType;
    billing_address_id: number;
    billing_address: ShippingLocationType;
    designer_id: number;
    designer: string;
    source: string;
    total: number;
    subtotal: number;
    bulk_discount: number;
    tax: number;
    cogs: number;
    total_cogs: number;
    salesmen_deduction: number;
    factory_deduction: number;
    salesmen_commission: number;
    profit: number;
    shipping_cost: number;
    use_credit: boolean;
    credit_to_use: number;
    customer_credit: number;
    source_id: number;
    is_refund: boolean;
    refund: number;
    order_notes: [];
    internal_order_number: string;
    order_payments: any[];
    total_amount_paid_exclusive_processing_fee: number;
    total_paid: number;
    marketplace_listed: number;
    platform: string;
    salesman_commission_paid: boolean;
    commission_formulas: any;
    processing_fees: number;
    commission_based_on: string;
    commission_type: string;
    vendors: any;
    transaction_fee: number;
    marketplace_service_charges: number;
    shipping_cost_real: number;
    salesman_cost: number;
    salesmen_commission_percentage: string;
    has_only_sizing_kits: boolean;
    payment_note: string;
    pagination: any;
    coupon_discount: number;
    tracking_sent: boolean;
    purchase_order_contact_info: any;
    order_vendors: any[];
    order_placed_with_factory: string;
    pf_notes: [];
    pf_status: number;
    test_print_status: TestPrintsStatus;
    refunds: any[];
    shipping_address: LocationType;
    created_by: UserType;
    logo_redraw_status: any;
    logo_redraw_id: number;
    coupon_code: any;
    test_print_timeliness: boolean;
    shipped_date: string;
    is_paid_out: boolean;
    has_wooter_shipped_order_to_factory: boolean;
    factory_order_details: any;
}

export const Order = Model<OrderType>('Order', {
    endpoint: 'apparel-orders',
})`
    id:Int;
    salesman_id:Int;
    salesman:${Salesman};
    customer_id:Int;
    customer:${Customer};
    promo_code_id:Int;
    subtotal:Float;
    bulk_discount:Float;
    total:Float;
    tax:Float;
    payment_method: Int;
    payment_data: Any;
    shipping_method_id:Int;
    isInternationalOrder:Boolean;
    location_id:Int;
    location:${Location};
    billing_address_id:Int;
    billing_address:${Location};
    discount:String;
    placement_date:String;
    status:String;
    order_factories:Any;
    completion_time_estimate:String;
    completion_time:String;
    type:String;
    invoice_number:String;
    items:Any;
    items:Any;
    current_stage:Any;
    stage_statuses:Any;
    important:String;
    salesmen_commission:Float;
    salesmen_deduction:Float;
    overdue:Boolean;
    category_id:Int;
    name:String;
    created_at:DateTime;
    shipping_cost:String;
    total_cogs:String;
    profit:String;
    salesmen_commission:String;
    notes:String;
    stripe_charge_id:String;
    designs_submitted:Boolean;
    roster_submitted:Boolean;
    quantity:Int;
    placed:Boolean;
    purchased:Boolean;
    payment_data:Any;
    shipping_method:Any;
    billing_address:Any;
    use_credit:Boolean;
    credit_to_use:Float;
    is_refund:Boolean;
    refund:Int;
    customer_credit:Float;
    internal_order_number:String;
    internal_order_number:String;
    total_amount_paid_exclusive_processing_fee:Float;
    total_paid:Float;
    salesman_commission_paid:boolean;
    commission_type:String;
    vendors:Any;
    transaction_fee:Float;
    amount_paid:Float;
    marketplace_service_charges:Float;
    has_only_sizing_kits:boolean;
    payment_note:String;
    coupon_discount:Float;
    tracking_sent: boolean;
    purchase_order_contact_info:Any;
    payment_status:String;
    coupon_id:Int;
    promo_code:string;
    order_vendors: Any;
    pf_notes:Any;
    pf_status:boolean;
    order_placed_with_factory:String;
    ready_at:Any;
    shipments: Any;
    has_tracking_number: boolean;
    ready_at: Any;
    total_items: Int;
    order_type_id: Int;
    production_file_count: Int;
    is_paid_out: Boolean;
    has_wooter_shipped_order_to_factory: Boolean;
    factory_order_details: Any;
`;

export interface ActiveOrderProps extends RouterComponentProps, ActiveProps<OrderType> {
    order?: number | OrderType;
    orderActiveRequest?: (id: number) => void;
}

export const IMPORTANT_VALUES = [
    { important: '1', name: 'Yes' },
    { important: '0', name: 'No' },
];

export const ORDER_TYPE_VALUES = [
    { order_type: 1, name: 'Standard Order' },
    { order_type: 2, name: 'Add-on Order' },
    { order_type: 3, name: 'Redo Order' },
    { order_type: 4, name: 'Re-order' },
    { order_type: 5, name: 'Custom Sample' },
];

export const REFUND_VALUES = [
    { is_refund: '1', name: 'Yes' },
    { is_refund: '0', name: 'No' },
];

export const UNPAID_PO_VALUES = [
    { unpaid_po: '1', name: 'Yes' },
    { unpaid_po: '0', name: 'No' },
];

export const PRODUCTION_FILES_STAUSES = [
    { pf_status: '1', name: 'Complete' },
    { pf_status: '0', name: 'Pending' },
];

export const TEST_PRINT_STATUSES = [
    { id: 1, name: 'Approved', test_print_status: '1' },
    { id: 2, name: 'Rejected', test_print_status: '2' },
    { id: 3, name: 'Pending Review', test_print_status: '4' },
    { id: 4, name: 'None', test_print_status: '5' },
    { id: 5, name: 'Not Required', test_print_status: '6' },
];

export const getOrderCategory = (order: OrderType): null | Partial<OrderType['category']> => {
    if (order.category) {
        return order.category;
    }

    if (order.category_id) {
        return {
            id: order.category_id,
        };
    }

    if (order.items?.length > 0) {
        let category: Partial<CategoryType> | null = null;
        order.items.forEach((item) => {
            if (!category) {
                if (item?.sku?.category) {
                    category = item.sku.category;
                } else if (item?.sku?.category_id) {
                    category = {
                        id: item?.sku?.category_id,
                    };
                }
            }
        });

        return category;
    }

    return null;
};

export const isRejectedMailACheckOrder = (order) =>
    !!(
        order.status === ORDER_STATUS.AWAITING_PAYMENT &&
        order?.order_payments?.length === 1 &&
        (order.order_payments[0].payment_method_id === PAYMENT_METHODS_TYPES.MAIL_A_CHECK ||
            order.order_payments[0].payment_method_id === PAYMENT_METHODS_TYPES.PAY_DIRECTLY_TO_WHOLESALER) &&
        order.order_payments[0].status === MAIL_A_CHECK_STATUS.REJECTED
    );

export const isRejectedDirectPaymentOrder = (order) =>
    !!(
        order.status === ORDER_STATUS.AWAITING_PAYMENT &&
        order?.order_payments?.length === 1 &&
        order.order_payments[0].payment_method_id === PAYMENT_METHODS_TYPES.PAY_DIRECTLY_TO_WHOLESALER &&
        order.order_payments[0].status === MAIL_A_CHECK_STATUS.REJECTED
    );
